import { PAGE_HEADING_ID } from '~/hooks/useRouteFocusAndTitle'

/** Marks the text input whose typed digits the Keypad should still handle. */
export const KEYPAD_INPUT_ATTRIBUTE = 'data-keypad-input'

const DIAL_CHARACTER_PATTERN = /^[0-9*#+]$/

const COMPOSITE_WIDGET_SELECTOR = [
  'menu',
  'menubar',
  'listbox',
  'combobox',
  'grid',
  'treegrid',
  'tree',
  'radiogroup',
  'dialog',
]
  .map((role) => `[role="${role}"]`)
  .join(', ')

export const isInCompositeWidget = (element: Element | null) =>
  !!element?.closest(COMPOSITE_WIDGET_SELECTOR)

const keypadHandledEvents = new WeakSet<KeyboardEvent>()

/** Records that the Keypad consumed (and prevented) this key press itself. */
export const markHandledByKeypad = (e: KeyboardEvent) => {
  keypadHandledEvents.add(e)
}

/** Default prevented by something other than the Keypad. */
export const isPreventedElsewhere = (e: KeyboardEvent) =>
  e.defaultPrevented && !keypadHandledEvents.has(e)

export const isDialCharacter = (key: string) => DIAL_CHARACTER_PATTERN.test(key)

/** A single visible character; Space is excluded so it still activates buttons. */
export const isPrintableCharacter = (key: string) =>
  key.length === 1 && key.trim() !== ''

export const hasModifierKey = (e: KeyboardEvent) =>
  e.ctrlKey || e.metaKey || e.altKey

export const isEditableElement = (
  element: EventTarget | Element | null,
): element is HTMLElement => {
  if (!(element instanceof HTMLElement)) return false
  if (element.isContentEditable) return true
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(element.tagName)
}

/**
 * True when no control has focus: focus is on `<body>`, on the route-change
 * page heading, or on a container that is only programmatically focusable
 * (`tabindex="-1"`, such as the skip-link `<main>` target, which mouse clicks
 * on empty space also focus) and encloses `region`.
 */
export const isFocusLost = (region?: Element | null) => {
  const active = document.activeElement
  if (!active || active === document.body) return true
  if (active.id === PAGE_HEADING_ID) return true
  return (
    !!region &&
    active !== region &&
    active.getAttribute('tabindex') === '-1' &&
    !isEditableElement(active) &&
    active.contains(region)
  )
}

/**
 * Whether a key press aimed at `target` belongs to a different control than
 * the keypad: an editable field (other than one marked with
 * `KEYPAD_INPUT_ATTRIBUTE`) or a composite widget that doesn't contain the
 * keypad.
 */
export const isKeyTargetOutsideKeypad = (
  target: EventTarget | null,
  keypad: HTMLElement | null,
) => {
  if (!(target instanceof Element)) return false
  if (isEditableElement(target)) {
    return !target.hasAttribute(KEYPAD_INPUT_ATTRIBUTE)
  }
  const widget = target.closest(COMPOSITE_WIDGET_SELECTOR)
  return !!widget && !(keypad && widget.contains(keypad))
}
